import { AxiosResponse } from 'axios';
import { useAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { alertNotificationAudio } from '../../assets/audio';
import { GoogleAnalyticsEvents } from '../../enum';
import { useCountdown } from '../../hooks/use-countdown';
import { reservationDataAtom } from '../../jotai-atoms/reservation-data.atom';
import { userAtom } from '../../jotai-atoms/user.atom';
import { CountdownState } from '../../models/CountdownState.type';
import { getNextLiveClassType } from '../../models/User';
import { LiveClassBookingService } from '../../services/liveclass-booking';
import { CountdownWidgetStateClassLater } from './CountdownWidgetStateClassLater';
import { CountdownWidgetStateStarted } from './CountdownWidgetStateClassStarted';
import { CountdownWidgetStateClassStarting } from './CountdownWidgetStateClassStarting';
import { CountdownWidgetStateCountdown } from './CountdownWidgetStateCountdown';
import { WidgetLiveClass } from './WidgetLiveClass.type';
import { WidgetSize } from './WidgetSize.type';

interface BaseCountdownWidgetProps {
  size?: WidgetSize;
  isThin?: boolean;
  className?: string;
}

const audioElement = new Audio(alertNotificationAudio);

export const CountdownWidget: React.FC<BaseCountdownWidgetProps> = ({
  className,
  isThin,
  size,
}) => {
  const [reservationData, setReservationData] = useAtom(reservationDataAtom);
  const [user] = useAtom(userAtom);

  const __ = useTranslation().t;

  const nextLiveClass = user?.nextLiveClass;
  const nextLiveClassDate = nextLiveClass?.startAt.substring(0, 19).replace('T', ' ') || '';

  const { diffInSeconds } = useCountdown({
    targetDate: nextLiveClassDate,
    timezone: user?.timezone,
  });

  const checkJoinIntervalInSeconds = 10;
  const checkLiveClassIntervalInSeconds = 60 * 5;
  const checkJoinInterval = useRef<NodeJS.Timeout | null>(null);
  const checkLiveClassInterval = useRef<NodeJS.Timeout | null>(null);
  const state = useRef<CountdownState>('class_later');

  useEffect(() => {
    if (state.current === 'class_later' || state.current === 'countdown') {
      setCurrentState();
    }
  }, [diffInSeconds]);

  useEffect(() => {
    if (state.current === 'class_started' && !user?.nextLiveClass) {
      clearInterval(checkLiveClassInterval.current as NodeJS.Timeout);
    }
  }, [state.current, user?.nextLiveClass]);

  useEffect(() => {
    getLiveClassDataRef.current = getLiveClassData;
  }, [reservationData]);

  useEffect(() => {
    setReservationData({
      ...reservationData,
      widgetState: state.current,
    });

    if (state.current === 'class_started') {
      audioElement.play();
    }
  }, [state.current]);

  const getLiveClassData = () => {
    setReservationData({
      ...reservationData,
      forceRefreshUserData: !reservationData?.forceRefreshUserData,
    });
  };

  const getLiveClassDataRef = useRef<any>(getLiveClassData);

  const setCurrentState = () => {
    if (diffInSeconds <= 5 * 60) {
      state.current = 'countdown';
    }
    if (diffInSeconds <= 2 * 60) {
      state.current = 'class_starting';
      startPollingCheckJoin();
    }
  };

  const startPollingCheckJoin = () => {
    checkJoinInterval.current = setInterval(function () {
      checkJoin();
    }, checkJoinIntervalInSeconds * 1000);
  };

  const checkJoin = () => {
    LiveClassBookingService.getCheckJoin().then((response) => {
      if (response.data.status === 'success') {
        state.current = 'class_started';
        clearInterval(checkJoinInterval.current as NodeJS.Timeout);
      }
    });
  };

  const handleJoinClick = () => {
    const windowRef = window.open('about:blank', '_blank');
    LiveClassBookingService.getJoinLink().then((response: AxiosResponse) => {
      if (response.data.status === 'success') {
        setReservationData({ ...reservationData, joinUrl: response.data.joinUrl });
        if (windowRef) {
          windowRef.location = response.data.joinUrl;
        } else {
          alert(__('whopsSomethingWentWrong'));
        }
        startPollingLiveClassData();
        ReactGA.event(GoogleAnalyticsEvents.JoinLiveClass);
      } else {
        alert(__('whopsSomethingWentWrong'));
      }
    });
  };

  const startPollingLiveClassData = () => {
    checkLiveClassInterval.current = setInterval(function () {
      getLiveClassDataRef.current();
    }, checkLiveClassIntervalInSeconds * 1000);
  };

  if (!nextLiveClass) {
    return null;
  }

  const liveClass: WidgetLiveClass = {
    dateTime: nextLiveClassDate,
    title: nextLiveClass.topic,
    type: getNextLiveClassType(nextLiveClass),
    liveClassId: nextLiveClass.liveClassId,
    preClassActivities: nextLiveClass.preClassActivities,
  };

  return (
    <Container size={size} className={className}>
      {state.current === 'class_later' && <CountdownWidgetStateClassLater liveClass={liveClass} />}
      {state.current === 'countdown' && (
        <CountdownWidgetStateCountdown liveClass={liveClass} isThin={isThin} size={size} />
      )}
      {state.current === 'class_starting' && (
        <CountdownWidgetStateClassStarting isThin={isThin} size={size} />
      )}
      {state.current === 'class_started' && (
        <CountdownWidgetStateStarted
          size={size}
          isThin={isThin}
          handleJoinClick={handleJoinClick}
        />
      )}
    </Container>
  );
};

interface ContainerProps {
  size?: WidgetSize;
}

const Container = styled.div<ContainerProps>`
  padding: ${({ size }) => (size === 'sm' ? '0 20px 0' : '0 40px 0')};
  width: ${({ size }) => (size === 'sm' ? '410px' : '470px')};
  position: relative;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.sm}) {
    width: 100%;
    ${({ size }) => (size === 'sm' ? `padding: 0 16px 0;` : `padding: 0 24px 0;`)}
  }

  @media screen and (min-width: ${({ theme }) => theme.screenSizes.sm}) and (max-width: ${({
      theme,
    }) => theme.screenSizes.lg}) {
    width: 100%;
    padding: 0 24px 0;
  }
`;
